import { useState } from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchStoreSummaries, mediaUrl } from '../../api';
import EmptyState from '../ui/EmptyState';
import styles from './StorePerformance.module.css';

/* CCTV-style SVG placeholder when no real image is available */
function CctvPlaceholder({ color = '#00A3FF', storeName = '' }) {
  const seed = storeName.charCodeAt(0) || 65;
  // Generate 3 simple figure positions deterministically
  const figs = [
    [40 + (seed % 20), 68],
    [100 + ((seed * 3) % 30), 65],
    [160 + ((seed * 7) % 25), 70],
  ];
  return (
    <svg viewBox="0 0 210 118" style={{ width: '100%', height: '100%', background: '#0d1117', display: 'block' }}>
      {/* Scan lines */}
      {Array.from({ length: 20 }, (_, i) => (
        <line key={i} x1="0" y1={i * 6} x2="210" y2={i * 6} stroke="rgba(255,255,255,0.015)" strokeWidth="1"/>
      ))}
      {/* Floor line */}
      <line x1="0" y1="78" x2="210" y2="78" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>
      {/* Figures */}
      {figs.map(([fx, fy], i) => (
        <g key={i}>
          <ellipse cx={fx} cy={fy + 2} rx="5.5" ry="7.5" fill={color} opacity="0.55"/>
          <circle cx={fx} cy={fy - 9} r="3.5" fill={color} opacity="0.55"/>
          <rect x={fx - 9} y={fy - 16} width="18" height="26" fill="none" stroke={color} strokeWidth="0.8" opacity="0.4" rx="1"/>
        </g>
      ))}
      {/* Corner brackets */}
      <path d="M5 14L5 6L13 6"  fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"/>
      <path d="M205 14L205 6L197 6" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"/>
      <path d="M5 104L5 112L13 112" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"/>
      <path d="M205 104L205 112L197 112" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5"/>
      {/* Cam label */}
      <rect x="5" y="5" width="54" height="11" rx="2" fill="rgba(0,0,0,0.5)"/>
      <text x="32" y="13" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="6" fontFamily="monospace">CUSTOMER VIEW</text>
    </svg>
  );
}

function FolderCard({ store, summary }) {
  const { enterStore } = useDashboard();
  const footfall = summary?.footfall || 0;
  const dwell    = summary?.dwell    || 0;
  const breaches = summary?.breaches || 0;
  const lastCap  = summary?.lastCapture || null;

  const bColor = breaches >= 10 ? 'var(--red)'   : breaches >= 7 ? 'var(--amber)'   : 'var(--green)';
  const bBg    = breaches >= 10 ? 'var(--red-bg)' : breaches >= 7 ? 'var(--amber-bg)' : 'var(--green-bg)';

  const imgSrc = mediaUrl(store.baseImage);

  return (
    <div className={styles.card} onClick={() => enterStore(store.name)}>
      {/* CCTV thumbnail */}
      <div className={styles.thumb}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={store.name}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <CctvPlaceholder color={store.color} storeName={store.name} />
        )}
        <div className={styles.thumbOverlay}>
          <span className={styles.camLbl}>CUSTOMER VIEW</span>
          <span className={`${styles.recBadge} ${store.active ? styles.recActive : styles.recInactive}`}>
            <span className={`${styles.recDot} ${store.active ? styles.recDotActive : ''}`}/>
            {store.active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
      </div>

      <div className={styles.name} title={`A2B ${store.name}`}>A2B {store.name}</div>

      <div className={styles.meta}>
        <div className={styles.stat}><span>Footfall</span><span>{footfall.toLocaleString()}</span></div>
        <div className={styles.stat}><span>Avg dwell time</span><span>{dwell} min</span></div>
        <div className={styles.stat}>
          <span>SOP Breaches</span>
          <span className={styles.breachPill} style={{ color: bColor, background: bBg }}>{breaches}</span>
        </div>
        {lastCap && (
          <div className={styles.stat}><span>Last capture</span><span>{lastCap}</span></div>
        )}
      </div>

      <div className={styles.cta}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5h6M5.5 2.5L8 5l-2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Open store view
      </div>
    </div>
  );
}

const PER_PAGE = 10;

export default function StorePerformance() {
  const { visibleStores, datesParam, startDate, endDate } = useDashboard();
  const [page, setPage]   = useState(1);
  const [query, setQuery] = useState('');

  const storeIds = visibleStores.map(s => s.storeId).join(',');
  const cacheKey = storeIds ? `summaries:${storeIds}:${datesParam}:${startDate}:${endDate}` : null;
  const { data: summaryData } = useCache(cacheKey, () => fetchStoreSummaries(storeIds, datesParam, startDate, endDate));
  const summaries = summaryData?.summaries || {};

  const filtered = visibleStores.filter(s => s.name.toLowerCase().includes(query.toLowerCase()));
  const pages    = Math.ceil(filtered.length / PER_PAGE) || 1;
  const current  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSearch(val) { setQuery(val); setPage(1); }

  return (
    <div className="section">
      <div className="sec-hdr">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--g900)', letterSpacing: '-0.2px', margin: 0 }}>Store Performance</h2>
          <p style={{ fontSize: 13, color: 'var(--g500)', marginTop: 4 }}>Select a store to view camera captures</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={styles.searchWrap}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className={styles.searchInput} value={query} onChange={e => handleSearch(e.target.value)} placeholder="Search stores…" />
          </div>
          <span className="count-badge">{filtered.length} stores</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No stores found"
          message={query ? `No stores match "${query}"` : 'No stores available for this client'}
        />
      ) : (
        <div className={styles.grid}>
          {current.map(s => <FolderCard key={s.name} store={s} summary={summaries[s.storeId]} />)}
        </div>
      )}

      {pages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.pInfo}>Showing {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, filtered.length)} of {filtered.length}</span>
          <div className={styles.pControls}>
            <button className={styles.pBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {Array.from({ length: pages }, (_, i) => (
              <button key={i} className={`${styles.pBtn} ${i+1 === page ? styles.pActive : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
            ))}
            <button className={styles.pBtn} disabled={page === pages} onClick={() => setPage(p => p + 1)}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
